import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { JwtHelperService } from '@auth0/angular-jwt';
import { Observable } from "rxjs";
import { map, shareReplay } from "rxjs/operators";
import { environment } from "../../../environments/environment";
import { User, UserForm } from "../models";
import { Login } from "../models/login.model";

export interface UserListItem {
  id: number;
  username: string;
  enabled: boolean;
  type: string;
}

export interface Page<T> {
  content: T[];
  number: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

export interface MinimalOption {
  id: number;
  text: string;
}

@Injectable()
export class UserService implements OnInit {

  private readonly apiRoot = environment.dns.replace(/\/?$/, '/');
  private readonly usersUrl = `${this.apiRoot}api/users`;

  jwtHelperService: JwtHelperService = new JwtHelperService();

  currentUser: User = null;
  user: User = new User();

  private salesForceOptions$?: Observable<MinimalOption[]>;
  private managerSiteOptions$?: Observable<MinimalOption[]>;

  constructor(private http: HttpClient, private router: Router) {}

  ngOnInit(): void {}

  login(login: Login): Observable<string> {
    return this.http.post(`${this.usersUrl}/signin`, login, { responseType: 'text' });
  }

  getCurrentUser(): Observable<User> {
    return this.http.get<User>(`${this.usersUrl}/whoami`);
  }

  getList(): Observable<User[]> {
    return this.http.get<User[]>(`${this.usersUrl}/list`);
  }

  getPagedList(page: number = 0, size: number = 25, q?: string): Observable<Page<UserListItem>> {
    let params = new HttpParams().set('page', page.toString()).set('size', size.toString());
    if (q && q.trim().length) params = params.set('q', q.trim());
    return this.http.get<Page<UserListItem>>(`${this.usersUrl}/list`, { params });
  }

  getOne(id: number): Observable<User> {
    return this.http.get<User>(`${this.usersUrl}/${id}`);
  }

  findUserByUserName(): Observable<User> {
    return this.http.get<User>(`${this.usersUrl}/userName/${this.getCurrentUserName()}`);
  }

  add(userForm: UserForm): Observable<User> {
    return this.http.post<User>(this.usersUrl, userForm);
  }

  update(userForm: UserForm): Observable<User> {
    return this.http.put<User>(this.usersUrl, userForm);
  }

  editState(id: number, isEnabled: boolean): Observable<User> {
    return this.http.put<User>(`${this.usersUrl}/${id}/edit-state/${isEnabled}`, null);
  }

  delete(id: number): Observable<boolean> {
    return this.http.delete<boolean>(`${this.usersUrl}/${id}`);
  }

  getToken(): string {
    const token = localStorage.getItem('token');
    return token ? token : '';
  }

  logout(): void {
    localStorage.removeItem('token');
    this.router.navigate(['/auth/login']);
  }

  isTokenExpired(): boolean {
    const token = localStorage.getItem('token');
    if (!token) {
      return true;
    }
    const tokenDecoded = this.jwtHelperService.decodeToken(token);
    if (tokenDecoded.exp) {
      return this.jwtHelperService.isTokenExpired(token);
    } else {
      return false;
    }
  }

  isHasRoles(roles: string[]): boolean {
    if (this.isTokenExpired()) return false;
    const tokenDecoded = this.jwtHelperService.decodeToken(localStorage.getItem('token'));
    for (let role of roles) {
      if (tokenDecoded.auth.find(auth => auth.authority === role)) {
        return true;
      }
    }
    return false;
  }

  getCurrentUserName(): string {
    const tokenDecoded = this.jwtHelperService.decodeToken(localStorage.getItem('token'));
    return tokenDecoded.sub;
  }

  getMinOptions(type: string, page: number = 0, size: number = 500, q?: string): Observable<MinimalOption[]> {
    let params = new HttpParams()
      .set('type', type)
      .set('page', page.toString())
      .set('size', size.toString());
    if (q && q.trim().length) params = params.set('q', q.trim());
    return this.http
      .get<Page<MinimalOption>>(`${this.usersUrl}/min`, { params })
      .pipe(map(res => res.content));
  }

  getSalesForceOptions(): Observable<MinimalOption[]> {
    if (!this.salesForceOptions$) {
      this.salesForceOptions$ = this.http
        .get<MinimalOption[]>(`${this.usersUrl}/sales-force-options`)
        .pipe(shareReplay(1));
    }
    return this.salesForceOptions$;
  }

  getManagerSiteOptions(): Observable<MinimalOption[]> {
    if (!this.managerSiteOptions$) {
      this.managerSiteOptions$ = this.http
        .get<MinimalOption[]>(`${this.usersUrl}/manager-site-options`)
        .pipe(shareReplay(1));
    }
    return this.managerSiteOptions$;
  }

  refreshOptionCaches(): void {
    this.salesForceOptions$ = undefined;
    this.managerSiteOptions$ = undefined;
  }

  getSalesForceIdsManagedBy(managerId: number): Observable<number[]> {
    return this.http.get<number[]>(`${this.usersUrl}/${managerId}/managed-sales-force-ids`);
  }

  getManagerSiteIdsManagedBy(directorId: number): Observable<number[]> {
    return this.http.get<number[]>(`${this.usersUrl}/${directorId}/managed-manager-site-ids`);
  }
}
